import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {MatIconRegistry} from "@angular/material/icon";
import {DomSanitizer} from "@angular/platform-browser";
import {ManageIndustriesService} from "@features/settings/industries/manage-industries.service";
import {PanelType} from '@shared-components/info-panel/info-panel.component';
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {UtilsService} from "@shared-services/utils.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {lastValueFrom, Subject, takeUntil} from "rxjs";
import {IndustryCategory, SelectableCategory} from "../../settings/industries/industry.model";
import {OfferingSpacesInterface, OfferingSpaceStatistics, SpaceType} from "../model/offering-spaces.interface";
import {ManageSpacesService} from "../service/manage-spaces.service";

@Component({
  selector: 'app-edit-single-space',
  templateUrl: './edit-single-space.component.html',
  styleUrls: ['./edit-single-space.component.scss']
})
export class EditSingleSpaceComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedSpace: OfferingSpacesInterface | null = null;
  @Input() addNewSpace  : Event | null = null;
  @Input() offeringUuid!: string;
  @Output('dirty') dirtyEvent = new EventEmitter<boolean>();
  @Output() spaceUpdated = new EventEmitter<OfferingSpacesInterface>();

  private unsubscribe$ = new Subject<void>();

  dirty = false;
  spaceDetailsForm!: FormGroup;
  statSpaceStatistics: OfferingSpaceStatistics | null = null;

  readonly MAX_PREFERRED_INDUSTRIES = 3;
  protected readonly PanelType = PanelType;

  preferredIndustriesForm!: FormGroup;

  allIndustries: SelectableCategory[] = [];

  constructor(public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private spaceService: ManageSpacesService,
              private matIconRegistry: MatIconRegistry,
              private domSanitizer: DomSanitizer,
              private fb: FormBuilder,
              private industriesService: ManageIndustriesService,
              private loader: LoadingService
              ) {

    this.matIconRegistry.addSvgIcon('person_home', this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/icons/spaces/person_home.svg'));
    this.matIconRegistry.addSvgIcon('stacked_lines', this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/icons/spaces/stacked_lines.svg'));
    this.matIconRegistry.addSvgIcon('circle_underscore_check', this.domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/icons/spaces/underscore_check_encircled.svg'));
  }

  ngOnInit() {
    this.loadIndustries();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: any) {
    if(changes.addNewSpace && changes.addNewSpace.currentValue !== null) {
      this.selectedSpace = {
        id: -1,
        districtUuid: '',
        spaceType: SpaceType.SOKO_COMPOUND,
        spaceName: '',
        spaceSize: 0,
        startDate: new Date(),
        endDate: null,
        locked: false,
        hasHadLease: false,
        earliestEndDate: null,
        latestStartDate: null,
        districtSpacesIndustries: []
      }
      this.cancelChanges();
      this.statSpaceStatistics = {
        id: -1,
        avgRentalPricingPerSqmValue: 0,
        avgPercentageOccupancy: 0,
        avgTerm: 0,
      }
    }

    if (changes.selectedSpace) {
      this.cancelChanges();
      if (this.selectedSpace) {
        this.loadSpaceStats();
        this.initPreferredIndustriesForm(this.selectedSpace!);
      }
    }

    this.emitDirtyFormEvent();
  }

  private resetSpaceDetailsForm(value: OfferingSpacesInterface): FormGroup {
    let startDate = this.dateUtils.displayShortDate(new Date());
    let endDate = null;

    if(value?.startDate) {
      startDate = this.dateUtils.displayShortDate(value.startDate);
    }

    if (value?.endDate) {
      // @ts-ignore
      endDate = this.dateUtils.displayShortDate(value.endDate);
    }
    const fg = new FormGroup({
      spaceName: new FormControl(value?.spaceName, [Validators.required, Validators.minLength(3)]),
      spaceSize: new FormControl(value?.spaceSize, [this.spaceSizeValidator]),
      startDate: new FormControl(startDate, [Validators.required]),
      endDate: new FormControl(endDate, [this.endDateValidator]) // This is optional, validator only checks if both dates exist
    });

    fg.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.emitDirtyFormEvent();
    });

    return fg;
  }

  private endDateValidator(control: AbstractControl): { [key: string]: any } | null {
    const startDate = control.root.get('startDate');
    const endDate = control.value;

    if (startDate && endDate) {
      const start = new Date(startDate.value);
      const end = new Date(endDate);

      if (end < start) {
        return { 'endDateBeforeStartDate': true };
      }
    }

    return null;
  }

  private spaceSizeValidator(control: AbstractControl): { [key: string]: any } | null {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return { 'required': true };
    }

    const numValue = Number(value);
    if (isNaN(numValue) || numValue <= 0) {
      return { 'invalidSize': true };
    }

    return null;
  }

  deleteSelectedSpace(): void {
    // lastValueFrom(this.spaceService.deleteSpace(this.selectedSpace!))
    //   .then(() => {
    //     this.showSpaceUpdate = false;
    //     this.showingActions = false;
    //
    //     if (this.selectedSpace!.locked) {
    //       let index: number = this.availableSpaces.findIndex(space => space.id === this.selectedSpace!.id);
    //       this.availableSpaces.splice(index, 1);
    //     } else {
    //       let index: number = this.unavailableSpaces.findIndex(space => space.id === this.selectedSpace!.id);
    //       this.unavailableSpaces.splice(index, 1);
    //     }
    //     this.selectedSpace = null;
    //     this.resetAllForms();
    //   })
    //   .catch((error: string) => {
    //     console.log(error);
    //   });
  }

  getSelectedSpaceSize(): string {
    return (this.selectedSpace) ? this.selectedSpace.spaceSize.toString() : '0';
  }

  getSpaceAveragePrice(): any {
    return (this.statSpaceStatistics) ? this.utils.displayCompactNumber(this.statSpaceStatistics.avgRentalPricingPerSqmValue) : 0
  }

  getAverageOccupancy(): any {
    return (this.statSpaceStatistics) ? this.utils.displayCompactNumber(this.statSpaceStatistics.avgPercentageOccupancy) : 0
  }

  getSpaceAverageTerm(): any {
    return (this.statSpaceStatistics) ? this.spaceService.displayDuration(this.statSpaceStatistics.avgTerm) : '0 months';
  }

  private loadIndustries() {
    this.loader.show();
    lastValueFrom(this.industriesService.getAllEnabledIndustries())
      .then((res: IndustryCategory[]) => {
        this.allIndustries = res.map(category => ({
          ...category,
          industries: (category.industries ?? []).map(ind => ({
            ...ind,
            selected: true,
          })),
          selected: true
        }));
      })
      .catch((error) => {
        console.error('Error loading industries:', error);
      })
      .finally(() => {
        this.loader.hide();
      })
  }

  get industriesArray(): FormArray {
    return this.preferredIndustriesForm?.get('industries') as FormArray;
  }

  private initPreferredIndustriesForm(space: OfferingSpacesInterface): void {
    this.preferredIndustriesForm = this.fb.group({
      industries: this.fb.array([])
    });

    const industries = space.districtSpacesIndustries ?? [];

    industries
      .slice(0, this.MAX_PREFERRED_INDUSTRIES)
      .forEach((industry) => {
        this.industriesArray.push(new FormControl<string>(industry, Validators.required));
      });

    if (space.locked) {
      this.preferredIndustriesForm.disable({ emitEvent: false });
    } else {
      this.preferredIndustriesForm.enable({ emitEvent: false });
    }

    this.industriesArray.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.updateIndustriesOnSpaceFromForm();
      });

    this.preferredIndustriesForm.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.emitDirtyFormEvent();
      });
  }

  private updateIndustriesOnSpaceFromForm(): void {
    if (!this.selectedSpace || !this.preferredIndustriesForm) {
      return;
    }

    this.selectedSpace.districtSpacesIndustries = (this.industriesArray.value || [])
      .filter((i: string): i is string => !!i)
      .map((i: string) => i);
  }

  cancelChanges(): void {
    this.dirty = false;

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.unsubscribe$ = new Subject<void>();

    if (this.selectedSpace) {
      this.spaceDetailsForm = this.resetSpaceDetailsForm(this.selectedSpace);
      this.initPreferredIndustriesForm(this.selectedSpace);
    }

    this.emitDirtyFormEvent();
  }

  saveSelectedSpace(): void {
    if (this.spaceDetailsForm.invalid || this.preferredIndustriesForm?.invalid) {
      this.spaceDetailsForm.markAllAsTouched();
      this.preferredIndustriesForm?.markAllAsTouched();
      return;
    }

    let updatedSpace = this.selectedSpace;
    if(this.spaceDetailsForm.dirty) {
      updatedSpace!.spaceName = this.spaceDetailsForm.get('spaceName')!.value;

      if(this.spaceDetailsForm.get('startDate')!.dirty) {
        updatedSpace!.startDate = this.spaceDetailsForm.get('startDate')!.value;
      }

      if (this.spaceDetailsForm.get('endDate')!.dirty) {
        updatedSpace!.endDate = this.spaceDetailsForm.get('endDate')!.value;
      }

      if(this.spaceDetailsForm.get('spaceSize')!.dirty) {
        updatedSpace!.spaceSize = this.spaceDetailsForm.get('spaceSize')!.value;
      }
    }

    updatedSpace?.id == -1 ? this.submitSpaceAdd(updatedSpace!) :
      this.submitSpaceUpdate(updatedSpace!);
  }

  // TODO: Check API code later - this should handle min/max dates based on existing leases
  // The API should validate date constraints based on lease periods
  getLatestStartDate(): any {
    return null; // Temporarily disabled - let API handle validation
  }

  private submitSpaceUpdate(space: OfferingSpacesInterface) {
    lastValueFrom(this.spaceService.updateSpace(space))
      .then((res: OfferingSpacesInterface) => {
        this.processSpaceUpdateResponse(res);
        this.resetSpaceDetailsForm(res);
        this.initPreferredIndustriesForm(res);
      })
      .catch(error => {
        console.log(error)
      });
  }

  private submitSpaceAdd(space: OfferingSpacesInterface) {
    const update = {
      ...space,
      id : null as any,
      districtUuid: null as any
    }

    lastValueFrom(this.spaceService.createNewSpace(update))
      .then((res: OfferingSpacesInterface) => {
        this.processSpaceUpdateResponse(res);
      })
      .catch(error => {
        console.log(error)
      });
  }

  private processSpaceUpdateResponse(res: OfferingSpacesInterface) {
    this.selectedSpace = res;
    this.dirty = false;
    this.dirtyEvent.emit(this.dirty);
    this.spaceUpdated.emit(res);
  }

  loadSpaceStats(): void {
    if (this.selectedSpace) {
      lastValueFrom(this.spaceService.getStatsForSpace(this.selectedSpace.id))
        .then((res: OfferingSpaceStatistics) => {
          this.statSpaceStatistics = res;
        })
        .catch((error: string) => {
          this.statSpaceStatistics = null;
          console.log(error)
        });
    }
  }

  addIndustrySlot(): void {
    if (!this.selectedSpace || this.selectedSpace.locked) {
      return;
    }

    if (this.industriesArray.length >= this.MAX_PREFERRED_INDUSTRIES) {
      return;
    }

    this.industriesArray.push(new FormControl<IndustryCategory | null>(null, Validators.required));
  }

  clearIndustrySelection(index: number): void {
    if (!this.selectedSpace || this.selectedSpace.locked) {
      return;
    }

    this.industriesArray.removeAt(index);
    this.updateIndustriesOnSpaceFromForm();
  }

  emitDirtyFormEvent(): void {
    const detailsDirty = this.spaceDetailsForm?.dirty === true;
    const industriesDirty = this.preferredIndustriesForm?.dirty === true;

    this.dirty = detailsDirty || industriesDirty;
    this.dirtyEvent.emit(this.dirty);
  }

  get isFormValid(): boolean {
    const detailsValid = this.spaceDetailsForm?.valid ?? false;
    const industriesValid = this.preferredIndustriesForm?.valid ?? true;
    return detailsValid && industriesValid;
  }

  get getDate() {
    return new Date();
  }
}
